import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Typography, Input, Rate, Spin, Result } from 'antd';
import { HomeOutlined, FireFilled } from '@ant-design/icons';
import { api } from '@/services/api';
import { API_ENDPOINTS } from '@/constants';
import type { ApiResponse, Session } from '@/types';

const { Title, Text } = Typography;
const { TextArea } = Input;

const COMFORT_LEVELS = [
  { score: 1, label: 'Lost',   color: '#E53935', bg: '#fff5f5' },
  { score: 2, label: 'Shaky',  color: '#F59E0B', bg: '#fffbeb' },
  { score: 3, label: 'Okay',   color: '#ca8a04', bg: '#fefce8' },
  { score: 4, label: 'Good',   color: '#16a34a', bg: '#f0fdf4' },
  { score: 5, label: 'Nailed', color: '#15803d', bg: '#dcfce7' },
];

function ComfortBreakdown({ interactions }: { interactions: Session['interactions'] }) {
  if (!interactions || interactions.length === 0) return null;

  const rated = interactions.filter((i) => i.comfortScore != null && i.response !== 'SKIPPED');
  if (rated.length === 0) return null;

  const counts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const i of rated) {
    if (i.comfortScore != null) counts[i.comfortScore] = (counts[i.comfortScore] ?? 0) + 1;
  }
  const total = rated.length;

  return (
    <div style={{ marginTop: 20, borderTop: '1px solid #f1f5f9', paddingTop: 16 }}>
      <Text style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 12 }}>
        Comfort Breakdown
      </Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {COMFORT_LEVELS.map(({ score, label, color }) => {
          const count = counts[score] ?? 0;
          const pct = total > 0 ? (count / total) * 100 : 0;
          return (
            <div key={score} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 52, display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <Text style={{ fontSize: 12, color: '#64748b' }}>{label}</Text>
              </div>
              <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${pct}%`,
                    background: color,
                    borderRadius: 99,
                    transition: 'width 0.5s ease',
                    minWidth: count > 0 ? 8 : 0,
                  }}
                />
              </div>
              <Text style={{ fontSize: 12, fontWeight: 600, color: count > 0 ? color : '#cbd5e1', width: 20, textAlign: 'right', flexShrink: 0 }}>
                {count}
              </Text>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatChip({
  icon,
  label,
  value,
  color,
  bg,
}: {
  icon: string;
  label: string;
  value: string | number;
  color: string;
  bg: string;
}) {
  return (
    <div
      style={{
        background: bg,
        borderRadius: 14,
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
      }}
    >
      <span style={{ fontSize: 20 }}>{icon}</span>
      <div>
        <div style={{ fontSize: 18, fontWeight: 700, color, lineHeight: 1.2 }}>{value}</div>
        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, marginTop: 1 }}>{label}</div>
      </div>
    </div>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function getScoreColor(score: number): string {
  if (score >= 70) return '#16a34a';
  if (score >= 40) return '#f59e0b';
  return '#ea580c';
}

function getScoreMessage(score: number): string {
  if (score >= 90) return 'Amazing work! You really know this stuff! 🎉';
  if (score >= 70) return 'Great job! Keep up the good work!';
  if (score >= 50) return 'Good effort! Review the tricky ones again.';
  return 'Nice try! Practice makes perfect. 💪';
}

function getStarCount(score: number): number {
  if (score >= 90) return 5;
  if (score >= 70) return 4;
  if (score >= 50) return 3;
  if (score >= 30) return 2;
  return 1;
}

export default function SessionCompletePage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const skippedFromState: number = (location.state as { skippedCount?: number } | null)?.skippedCount ?? 0;
  const queryClient = useQueryClient();

  const [comment, setComment] = useState('');
  const [isCompleted, setIsCompleted] = useState(false);

  const { data: sessionData, isLoading, error } = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => api.get<never, ApiResponse<Session>>(`${API_ENDPOINTS.SESSIONS}/${sessionId}`),
    enabled: !!sessionId,
  });

  const session = sessionData?.data;

  useEffect(() => {
    if (session?.completedAt) {
      setIsCompleted(true);
      if (session.comment) setComment(session.comment);
    }
  }, [session]);

  const completeMutation = useMutation({
    mutationFn: () =>
      api.post<never, ApiResponse<Session>>(
        `${API_ENDPOINTS.SESSIONS}/${sessionId}/complete`,
        { comment: comment.trim() || undefined },
      ),
    onSuccess: (response) => {
      queryClient.setQueryData(['session', sessionId], response);
      queryClient.invalidateQueries({ queryKey: ['student', 'decks'] });
      setIsCompleted(true);
    },
  });

  if (isLoading) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#faf8f5', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <Spin size="large" />
        <Text style={{ marginTop: 16, fontSize: 16, color: '#64748b' }}>Loading your results...</Text>
      </div>
    );
  }

  if (error || !session) {
    return (
      <div style={{ position: 'fixed', inset: 0, background: '#faf8f5', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
        <Result
          status="error"
          title="Session not found"
          subTitle="We could not find this review session."
          extra={<Button type="primary" size="large" onClick={() => navigate('/student')}>Back to Home</Button>}
        />
      </div>
    );
  }

  const totalCards = session.cardsReviewed || 0;
  const skippedCount = session.cardsSkipped ?? skippedFromState;
  const needHelp = session.cardsNotUnderstood;
  const score = totalCards > 0 ? Math.round((session.cardsUnderstood / totalCards) * 100) : 0;
  const scoreColor = getScoreColor(score);
  const stars = getStarCount(score);

  const ScoreRing = () => (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 20 }}>
      <div
        style={{
          width: 120,
          height: 120,
          borderRadius: '50%',
          background: `conic-gradient(${scoreColor} ${score * 3.6}deg, #f1f5f9 0deg)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 12,
          boxShadow: `0 0 0 4px white, 0 0 0 6px ${scoreColor}22`,
        }}
      >
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: '50%',
            background: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <span style={{ fontSize: 28, fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{score}%</span>
          <span style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>score</span>
        </div>
      </div>
      <Rate disabled value={stars} style={{ fontSize: 22 }} />
    </div>
  );

  // Pre-completion: show summary + comment form
  if (!isCompleted) {
    return (
      <div
        style={{
          position: 'fixed', inset: 0,
          background: 'linear-gradient(160deg, #f0f9ff 0%, #faf8f5 60%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, overflow: 'auto', padding: '24px 16px',
        }}
      >
        <div style={{ maxWidth: 480, width: '100%' }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>🎉</div>
            <Title level={2} style={{ marginBottom: 4, color: '#1e293b', fontSize: 28 }}>You did it!</Title>
            <Text style={{ fontSize: 15, color: '#64748b' }}>
              You reviewed all <strong>{totalCards}</strong> cards
            </Text>
          </div>

          {/* Main result card */}
          <div style={{ background: 'white', borderRadius: 20, padding: 24, marginBottom: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
            <ScoreRing />

            {/* Stats grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 0 }}>
              <StatChip icon="✅" label="Understood" value={session.cardsUnderstood} color="#16a34a" bg="#f0fdf4" />
              <StatChip icon="❌" label="Need Help"  value={needHelp}                color="#dc2626" bg="#fef2f2" />
              <StatChip icon="⏭️" label="Skipped"    value={skippedCount}            color="#64748b" bg="#f8fafc" />
              <StatChip icon="⏱️" label="Duration"   value={formatDuration(session.durationSeconds)} color="#6366f1" bg="#f5f3ff" />
            </div>

            <ComfortBreakdown interactions={session.interactions} />
          </div>

          {/* Feedback card */}
          <div style={{ background: 'white', borderRadius: 20, padding: 20, marginBottom: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)' }}>
            <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4, color: '#374151' }}>
              💬 Feedback for your teacher <Text type="secondary" style={{ fontWeight: 400, fontSize: 13 }}>(optional)</Text>
            </Text>
            <TextArea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="How was this deck? Anything confusing?"
              maxLength={500}
              showCount
              rows={3}
              style={{ fontSize: 14, borderRadius: 10, marginTop: 8 }}
            />
          </div>

          <Button
            type="primary"
            size="large"
            block
            onClick={() => completeMutation.mutate()}
            loading={completeMutation.isPending}
            style={{ height: 52, borderRadius: 14, fontSize: 16, fontWeight: 600, background: '#6366f1', borderColor: '#6366f1' }}
          >
            Submit &amp; Finish
          </Button>
        </div>
      </div>
    );
  }

  // Post-completion: celebration screen
  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'linear-gradient(160deg, #f0f9ff 0%, #faf8f5 50%, #f0fdf4 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, overflow: 'auto', padding: '24px 16px',
      }}
    >
      <div style={{ maxWidth: 440, width: '100%', textAlign: 'center' }}>
        {/* Header */}
        <FireFilled style={{ fontSize: 44, color: '#f59e0b', display: 'block', marginBottom: 12 }} />
        <Title level={2} style={{ marginBottom: 4, color: '#1e293b', fontSize: 28 }}>
          {score >= 70 ? 'Great Job!' : score >= 40 ? 'Well Done!' : 'Nice Try!'}
        </Title>
        <Text style={{ fontSize: 15, color: '#64748b', display: 'block', marginBottom: 28 }}>
          {getScoreMessage(score)}
        </Text>

        {/* Result card */}
        <div style={{ background: 'white', borderRadius: 24, padding: 28, marginBottom: 24, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', textAlign: 'left' }}>
          {/* Score ring centered */}
          <div style={{ textAlign: 'center' }}>
            <ScoreRing />
            <Text style={{ fontSize: 14, color: '#94a3b8', display: 'block', marginBottom: 20 }}>
              {totalCards} cards reviewed
            </Text>
          </div>

          {/* Stats grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 0 }}>
            <StatChip icon="✅" label="Understood" value={session.cardsUnderstood} color="#16a34a" bg="#f0fdf4" />
            <StatChip icon="❌" label="Need Help"  value={needHelp}                color="#dc2626" bg="#fef2f2" />
            <StatChip icon="⏭️" label="Skipped"    value={skippedCount}            color="#64748b" bg="#f8fafc" />
            <StatChip icon="⏱️" label="Duration"   value={formatDuration(session.durationSeconds)} color="#6366f1" bg="#f5f3ff" />
          </div>

          <ComfortBreakdown interactions={session.interactions} />
        </div>

        <Button
          type="primary"
          size="large"
          icon={<HomeOutlined />}
          onClick={() => {
            queryClient.invalidateQueries({ queryKey: ['student', 'decks'] });
            navigate('/student');
          }}
          style={{ height: 52, minWidth: 200, borderRadius: 14, fontSize: 16, fontWeight: 600, background: '#6366f1', borderColor: '#6366f1' }}
        >
          Back to Home
        </Button>
      </div>
    </div>
  );
}
